import React, { useState, useRef, useEffect } from 'react';
import './GdprConsentModal.css';
import myshieldLogo from '../assets/myshield-logo.png';
import { PORTAL_VERSION, PORTAL_VERSION_DATE } from '../version';

interface GdprConsentModalProps {
  onAccept: () => void;
  userRole?: string;
}

const GdprConsentModal: React.FC<GdprConsentModalProps> = ({ onAccept, userRole = '' }) => {
  const isClient = userRole === 'Client';
  const [step, setStep]                         = useState<1 | 2>(1);
  const [agreed, setAgreed]                     = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const step1Ref = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);

  // When step 2 mounts check if content already fits without scrolling
  useEffect(() => {
    if (step === 2) {
      setHasScrolledToBottom(false);
      setAgreed(false);
      setTimeout(() => {
        const el = step2Ref.current;
        if (el && el.scrollHeight <= el.clientHeight + 20) {
          setHasScrolledToBottom(true);
        }
        el?.scrollTo({ top: 0 });
      }, 50);
    }
  }, [step]);

  const handleStep2Scroll = () => {
    const el = step2Ref.current;
    if (!el || hasScrolledToBottom) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <div className="gdpr-overlay">
      <div className="gdpr-modal">

        {/* ── Shared header ── */}
        <div className="gdpr-header">
          <img src={myshieldLogo} alt="Myshield" className="gdpr-logo" />
          <div style={{ flex: 1 }}>
          <h1 className="gdpr-title">
              {step === 1
                ? 'Privacy & Data Notice'
                : isClient
                  ? 'Portal Terms of Use & Your Responsibilities'
                  : 'Usage Policy, Fair Use & TCF Declaration'}
            </h1>
            <p className="gdpr-subtitle">Myshield — Fleet Management Environment by Yourshield</p>
          </div>
          <div className="gdpr-step-badge">Step {step} of 2</div>
        </div>

        {/* ══════════════════════════════════════════
            STEP 1 — Privacy & Data Notice
        ══════════════════════════════════════════ */}
        {step === 1 && (
          <>
            <div className="gdpr-scroll-body" ref={step1Ref}>
              <p className="gdpr-intro">
                Before you access the Environment, please read how Yourshield collects, uses and
                protects your personal data in accordance with the&nbsp;
                <strong>UK General Data Protection Regulation (UK GDPR)</strong> and the&nbsp;
                <strong>Data Protection Act 2018</strong>.
              </p>

              <h3>1. Who We Are</h3>
              <p>
                Yourshield Limited (&ldquo;Yourshield&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) operates the Myshield Environment.
                Yourshield is the <strong>Data Controller</strong> responsible for your personal data
                processed through this system. Registered in England &amp; Wales.
              </p>
              <p>For data protection queries contact our Data Protection Officer at <strong>riskandcompliance@yourshield.co.uk</strong>.</p>

              <h3>2. What Data We Collect</h3>
              <p>When you use Myshield we may collect and process the following categories of personal data:</p>
              <ul>
                <li><strong>Account data</strong> — name, email address, username, role and login timestamps.</li>
                <li><strong>Fleet data</strong> — vehicle registrations, make, model, vehicle type, value, cover status and inception dates.</li>
                <li><strong>Driver data</strong> — driver names, date of birth, licence numbers, licence categories and expiry dates.</li>
                <li><strong>Site data</strong> — business site names and postal addresses linked to your fleet.</li>
                <li><strong>Activity data</strong> — audit logs of all Environment actions including username and timestamp.</li>
                <li><strong>Technical data</strong> — session tokens (JWT) stored in browser local storage for authentication only. No tracking cookies are used.</li>
              </ul>

              <h3>3. How We Use Your Data</h3>
              <p>Your data is processed under Article 6 of UK GDPR for the following purposes:</p>
              <ul>
                <li><strong>Contract performance</strong> — to provide and manage fleet insurance and cover services on your behalf.</li>
                <li><strong>Legal obligation</strong> — to maintain accurate insurance records and comply with UK insurance regulation.</li>
                <li><strong>Legitimate interests</strong> — to maintain a secure, auditable Environment and improve our services.</li>
                <li><strong>Consent</strong> — for communications where you have opted in.</li>
              </ul>

              <h3>4. Who We Share Your Data With</h3>
              <p>We do not sell your personal data. Your data may be shared only with:</p>
              <ul>
                <li>Yourshield staff who administer the Environment and manage your policy.</li>
                <li>Authorised insurers and underwriters for the purpose of arranging and managing fleet cover.</li>
                <li>IT infrastructure providers acting as data processors under appropriate contractual safeguards.</li>
                <li>Regulatory bodies or law enforcement where legally required.</li>
              </ul>

              <h3>5. Data Retention</h3>
              <p>
                We retain your personal data for the duration of your active policy and account, plus
                a further <strong>7 years</strong> as required for insurance regulatory compliance.
                Account data is retained for 2 years following deactivation, then securely deleted or anonymised.
              </p>

              <h3>6. Your Rights Under UK GDPR</h3>
              <ul>
                <li><strong>Right of access</strong> — request a copy of data we hold about you.</li>
                <li><strong>Right to rectification</strong> — request correction of inaccurate data.</li>
                <li><strong>Right to erasure</strong> — request deletion where no legal basis for retention exists.</li>
                <li><strong>Right to restriction</strong> — request limits on how we process your data.</li>
                <li><strong>Right to data portability</strong> — receive your data in a machine-readable format.</li>
                <li><strong>Right to object</strong> — object to processing based on legitimate interests.</li>
                <li><strong>Automated decision-making</strong> — this Environment makes no automated decisions with legal or significant effect.</li>
              </ul>
              <p>
                To exercise any right, email <strong>riskandcompliance@yourshield.co.uk</strong>. You may also
                lodge a complaint with the <strong>Information Commissioner&rsquo;s Office (ICO)</strong> at&nbsp;
                <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.
              </p>

              <h3>7. Security</h3>
              <p>
                We implement appropriate technical and organisational measures including encrypted
                transmissions (HTTPS), token-based authentication, role-based access controls and a
                full audit trail of all Environment activity.
              </p>

              <h3>8. Cookies &amp; Local Storage</h3>
              <p>
                This Environment does not use tracking cookies. A single JWT session token is stored in
                browser local storage solely to authenticate your session and is removed on logout.
                No third-party analytics or advertising scripts are loaded.
              </p>

              <h3>9. Changes to This Policy</h3>
              <p>
                We may update this notice from time to time. Where changes are material you will be
                asked to review and re-acknowledge the updated policy on your next login.
              </p>

              <p className="gdpr-policy-version">
                Last updated: {PORTAL_VERSION_DATE} &middot; Portal v{PORTAL_VERSION} &middot; Yourshield Limited
              </p>
            </div>

            <div className="gdpr-footer">
              <button className="gdpr-accept-btn" onClick={() => setStep(2)}>
                {isClient ? 'Next: Portal Terms of Use →' : 'Next: Usage Policy & TCF Declaration →'}
              </button>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            STEP 2 — Varies by role:
            Admin/Staff: Usage, Fair Use & TCF Declaration
            Client: Policy, Cover Disclaimers & Terms of Use
        ══════════════════════════════════════════ */}
        {step === 2 && (
          <>
            {!hasScrolledToBottom && (
              <div className="gdpr-scroll-notice">
                ↓ Please scroll through all of the content below before accepting.
              </div>
            )}

            <div
              className="gdpr-scroll-body"
              ref={step2Ref}
              onScroll={handleStep2Scroll}
            >
              {isClient ? (
                /* ── CLIENT VERSION ─────────────────────────────────────────── */
                <>
                  <p className="gdpr-intro">
                    You are accessing the Myshield portal as a registered client of Yourshield Limited.
                    These terms explain how you may use this portal and what we need from you to ensure
                    your fleet insurance is managed accurately and without disruption to your cover.
                  </p>

                  <h3>1. Your Account &amp; Access</h3>
                  <p>
                    Your login is personal to you as an authorised representative of your organisation.
                    Do not share your username or password with anyone else. You are responsible for all
                    activity carried out under your account. If you believe your login details have been
                    compromised, contact Yourshield immediately at <strong>riskandcompliance@yourshield.co.uk</strong> so
                    we can suspend and reset your access without delay.
                  </p>

                  <h3>2. Accuracy of Information</h3>
                  <p>
                    You are responsible for ensuring that all vehicle, driver and business information
                    submitted through this portal is accurate, complete and up to date. Submitting false
                    or misleading information may invalidate your insurance cover. Yourshield cannot be
                    held responsible for cover failing to respond where inaccurate information has been
                    provided by your organisation.
                  </p>

                  <h3>3. Cover Confirmation</h3>
                  <p>
                    Submitting a request through this portal is not confirmation that cover has been
                    granted. Cover only takes effect from the date and time confirmed by Yourshield in
                    writing. Do not operate any vehicle on the road until you have received written
                    confirmation of cover from Yourshield. If you are unsure whether a vehicle is on
                    cover, contact your account manager before it is used.
                  </p>
                  <p>
                    Yourshield reserves the right to decline any cover request that does not meet the
                    underwriting criteria of your policy. You will be notified of any decision through
                    the portal.
                  </p>

                  <h3>4. What You May Do</h3>
                  <p>Using this portal, you may:</p>
                  <ul>
                    <li>Submit requests to add or remove vehicles from your fleet policy.</li>
                    <li>Request temporary cover for vehicles where your policy permits.</li>
                    <li>Communicate with your Yourshield account team via the portal messaging system.</li>
                  </ul>

                  <h3>5. What You Must Not Do</h3>
                  <ul>
                    <li>Submit vehicle, driver or usage information you know to be inaccurate or incomplete.</li>
                    <li>Attempt to access another organisation&rsquo;s account or data.</li>
                    <li>Share your portal login with anyone outside your organisation or with third parties.</li>
                    <li>Use automated software, scripts or bots to submit requests or interact with the portal.</li>
                    <li>Request backdated cover outside the terms of your existing policy.</li>
                  </ul>

                  <h3>6. Driver &amp; Personal Data</h3>
                  <p>
                    When you submit information about named drivers or other individuals through this
                    portal, you are confirming that you have a lawful basis to share that personal data
                    with Yourshield under UK data protection law. Driver data submitted by you is used
                    solely for the purpose of administering your fleet insurance policy and will not be
                    shared beyond what is necessary for that purpose.
                  </p>

                  <h3>7. Scope of This Portal</h3>
                  <p>
                    This portal is an administrative tool for managing your fleet insurance with
                    Yourshield. Nothing within it constitutes insurance advice. Your cover is governed
                    by the terms and conditions of your policy documents. If you need guidance on the
                    scope of your cover or whether a specific vehicle or risk is eligible, contact your
                    Yourshield account manager before submitting a request.
                  </p>

                  <h3>8. Security</h3>
                  <p>
                    Your session on this portal is protected by an encrypted, time-limited login token
                    that expires automatically after a period of inactivity. You must log out of the
                    portal whenever you are finished, particularly when using a shared or unattended
                    device. All activity on your account is logged for security and audit purposes.
                  </p>

                  <h3>9. Changes to These Terms</h3>
                  <p>
                    We may update these terms from time to time. Where changes are material you will be
                    asked to review and re-accept the updated terms on your next login.
                  </p>

                  <p className="gdpr-policy-version">
                    Last updated: {PORTAL_VERSION_DATE} &middot; Portal v{PORTAL_VERSION} &middot; Yourshield Limited &middot; FRN: 617848
                  </p>
                </>
              ) : (
                /* ── INTERNAL STAFF VERSION ─────────────────────────────────── */
                <>
                  <p className="gdpr-intro">
                    <strong>Important:</strong> This declaration governs your use of the Myshield portal as a
                    member of the Yourshield team. Please read carefully and scroll to the bottom to accept.
                  </p>

                  <h3>1. System Usage Policy</h3>
                  <p>The Myshield portal is a business-critical tool used to manage fleet insurance mid-term adjustments (MTAs), client data and insurer communications. Access is restricted to authorised Yourshield employees and appointed representatives only.</p>
                  <p>You are granted access to the portal in connection with your role at Yourshield Limited. Access is personal, non-transferable and must not be shared. You are responsible for all actions carried out under your user account. If you suspect your credentials have been compromised, you must notify the Myshield team immediately so access can be suspended and credentials reset.</p>
                  <p>Unauthorised access to the system, or use of the system for purposes outside your defined role, may constitute a criminal offence under the Computer Misuse Act 1990 and will be treated as a serious disciplinary matter.</p>

                  <h3>2. Fair Usage &amp; Acceptable Use</h3>
                  <p>You agree to use the Myshield portal only for legitimate business purposes in connection with your duties at Yourshield Limited. Specifically, you must:</p>
                  <ul>
                    <li>Process only genuine, accurate insurance instructions and mid-term adjustments on behalf of policyholders.</li>
                    <li>Ensure all vehicle additions, removals, reinstatements and cover changes reflect the true insurance risk being presented to the insurer.</li>
                    <li>Use audit trails and consent records only for their intended compliance and customer service purposes.</li>
                    <li>Maintain the confidentiality of all client, driver and vehicle data accessible through the portal.</li>
                    <li>Report any suspected errors, data breaches or system misuse to your line manager and the Data Protection Officer (<strong>riskandcompliance@yourshield.co.uk</strong>) immediately.</li>
                  </ul>
                  <p>You must not:</p>
                  <ul>
                    <li>Access, amend or delete records that are not within your authorised remit.</li>
                    <li>Backdate cover or amend inception dates without the appropriate written justification and required approval, in line with the system controls in place.</li>
                    <li>Attempt to override, circumvent or disable any access controls, audit logs or security measures within the portal.</li>
                    <li>Share, export or copy client data outside of the portal except where strictly required for your job function and in compliance with Yourshield&rsquo;s data protection policy.</li>
                    <li>Use automated tools, scripts or bots to interact with the portal.</li>
                  </ul>

                  <h3>3. Treating Customers Fairly (TCF) Declaration</h3>
                  <p>As a regulated firm authorised by the Financial Conduct Authority (FRN: 617848), Yourshield Limited is required to demonstrate that all staff act in accordance with the FCA&rsquo;s Treating Customers Fairly principle at all times. By accepting this declaration, you confirm your commitment to the following TCF outcomes:</p>
                  <ul>
                    <li><strong>Outcome 1 — Culture:</strong> Customers can be confident that fair treatment is central to your work within this portal.</li>
                    <li><strong>Outcome 2 — Products &amp; Services:</strong> You will only facilitate insurance cover that genuinely meets the needs of the client&rsquo;s fleet risk, and will not approve requests where the risk does not meet underwriting criteria.</li>
                    <li><strong>Outcome 3 — Clear Communication:</strong> Any decisions communicated through the portal (approvals, rejections, cover letters) will be accurate, clear and not misleading.</li>
                    <li><strong>Outcome 4 — Suitable Advice:</strong> You will not provide insurance advice beyond what is required for the administration of the fleet policy.</li>
                    <li><strong>Outcome 5 — Performance Standards:</strong> You will process workstream items, approvals and client requests within agreed service levels, without causing unnecessary delay that could prejudice cover.</li>
                    <li><strong>Outcome 6 — No Barriers:</strong> You will not create unreasonable barriers for clients making adjustments to their policy or accessing information about their cover.</li>
                  </ul>
                  <p>You acknowledge that TCF MI evidence is generated quarterly from this portal and reviewed by senior management. Your activity within the portal may form part of this evidence pack.</p>

                  <h3>4. Security &amp; Compliance Acceptance</h3>
                  <p>You confirm that you have read, understood and accept the following security controls that are in place on this portal:</p>
                  <ul>
                    <li><strong>Multi-Factor Authentication (MFA):</strong> MFA is mandatory for all admin-tier accounts. You must not attempt to bypass MFA or share MFA codes.</li>
                    <li><strong>Session Security:</strong> Sessions automatically expire after 30 minutes of inactivity. You must log out of the portal when leaving your workstation unattended.</li>
                    <li><strong>Account Lockout:</strong> Accounts are locked after 5 consecutive failed login attempts. Contact the Myshield team to unlock.</li>
                    <li><strong>Audit Trail:</strong> All actions within the portal are recorded with your user ID and timestamp. These logs are retained for compliance purposes and may be reviewed by senior management or the FCA.</li>
                    <li><strong>Role-Based Access:</strong> You have access to data and functions appropriate to your role. Attempting to access data or functions outside your role level is a breach of this agreement.</li>
                    <li><strong>Data Classification:</strong> Client, vehicle and driver data within this portal is classified as confidential. It must be handled in accordance with Yourshield&rsquo;s Information Security Policy.</li>
                    <li><strong>Incident Reporting:</strong> Any suspected data breach, unauthorised access or security incident must be reported immediately to the Data Protection Officer and, where required, to the ICO within 72 hours.</li>
                    <li><strong>SYSC 3 Attestation:</strong> Senior management must complete the annual SYSC 3 systems and controls attestation within the Security &amp; Compliance section of the portal.</li>
                  </ul>

                  <h3>5. Conduct Standards &amp; Disciplinary Consequences</h3>
                  <p>Breach of this declaration, the Acceptable Use Policy or the TCF commitments above may result in:</p>
                  <ul>
                    <li>Immediate suspension of portal access pending investigation.</li>
                    <li>Disciplinary action under Yourshield&rsquo;s HR policies, up to and including dismissal.</li>
                    <li>Referral to the FCA where the breach constitutes a regulatory failing.</li>
                    <li>Legal action where the breach causes loss to Yourshield, its clients or their insurers.</li>
                  </ul>

                  <h3>6. Acknowledgement of Ongoing Obligations</h3>
                  <p>This declaration remains in effect for the duration of your access to the Myshield portal. It is renewed annually and each time you log in. Changes to this declaration will be communicated to you before they take effect, and you will be required to re-accept on your next login.</p>
                  <p>By accepting below, you confirm that you have read and understood this declaration in full, and that you commit to upholding these standards in your use of the Myshield portal.</p>

                  <p className="gdpr-policy-version">
                    Last updated: {PORTAL_VERSION_DATE} &middot; Portal v{PORTAL_VERSION} &middot; Yourshield Limited &middot; FRN: 617848
                  </p>
                </>
              )}
            </div>

            {/* Scroll-gated checkbox */}
            <label className={`gdpr-checkbox-label ${!hasScrolledToBottom ? 'gdpr-checkbox-label--locked' : ''}`}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => hasScrolledToBottom && setAgreed(e.target.checked)}
                disabled={!hasScrolledToBottom}
                className="gdpr-checkbox"
              />
              <span>
                {hasScrolledToBottom
                  ? isClient
                    ? 'I confirm I am an authorised representative of my organisation and that I have read and agree to these Portal Terms of Use. I understand that cover requests are not confirmed until Yourshield provides written confirmation.'
                    : 'I have read and agree to the Myshield Usage Policy, Fair Use Policy, TCF Declaration and Security & Compliance Acceptance above. I confirm I am an authorised Yourshield team member and will use this portal in accordance with these obligations.'
                  : 'You must scroll through and read all of the content above before you can accept.'}
              </span>
            </label>
            <section className="gdpr-session-storage-section">
              <h3>Session &amp; Data Storage</h3>
              <p>
                Myshield uses JSON Web Tokens (JWT) stored in browser local storage to maintain your authenticated session. This approach is a recognised industry practice and is managed as an accepted risk under our Information Security Policy. Mitigating controls include: HTTPS-only transmission, automatic session expiry, logout token clearance, and Content Security Policy headers. For full details see our{' '}
                <a href="https://www.yourshield.co.uk/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
            </section>

            <div className="gdpr-footer">
              <button
                className="gdpr-back-btn"
                onClick={() => setStep(1)}
              >
                &larr; Back
              </button>
              <button
                className="gdpr-accept-btn"
                disabled={!agreed}
                onClick={onAccept}
              >
                Accept &amp; Enter Myshield
              </button>
              {!hasScrolledToBottom && (
                <p className="gdpr-hint">Scroll through all content to unlock acceptance.</p>
              )}
              {hasScrolledToBottom && !agreed && (
                <p className="gdpr-hint">Please tick the box above to continue.</p>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default GdprConsentModal;
